const APP_STORAGE_VERSION = "2026.03.07-storage-v1";
const APP_BUILD_VERSION = "2026.03.07-build-v1";
const LAST_CLEANUP_KEY = "last_cleanup_run";
const LAST_DEEP_CLEANUP_KEY = "last_deep_cleanup";
const STORAGE_VERSION_KEY = "app_storage_version";
const BUILD_VERSION_KEY = "app_build_version";
const BUILD_RELOAD_GUARD_KEY = "rm_build_reload_guard";
const SELF_HEAL_GUARD_KEY = "rm_last_self_heal";
const API_FAILURE_COUNT_KEY = "rm_api_failure_count";
const API_FAILURE_TS_KEY = "rm_api_failure_ts";
const STANDARD_CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000;
const DEEP_CLEANUP_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
const API_FAILURE_WINDOW_MS = 10 * 60 * 1000;
const API_FAILURE_THRESHOLD = 5;
const SELF_HEAL_COOLDOWN_MS = 6 * 60 * 60 * 1000;
const MAX_STORAGE_USAGE_BYTES = 40 * 1024 * 1024;
const CURRENT_CACHE_NAMES = [
  "redmagma-shell-v2026-03-07-1",
  "redmagma-runtime-v2026-03-07-1",
];
const KNOWN_LOCAL_KEYS = new Set([
  "auth",
  "role",
  "rm_last_category",
  "redmagma_favorites",
  LAST_CLEANUP_KEY,
  LAST_DEEP_CLEANUP_KEY,
  STORAGE_VERSION_KEY,
  BUILD_VERSION_KEY,
]);
const TEMP_LOCAL_KEY_RULES = [
  { key: "rm_stock_cache_v1", ttlMs: STANDARD_CLEANUP_INTERVAL_MS },
  { key: "rm_recent_v1", ttlMs: DEEP_CLEANUP_INTERVAL_MS },
  { key: API_FAILURE_COUNT_KEY, ttlMs: API_FAILURE_WINDOW_MS },
  { key: API_FAILURE_TS_KEY, ttlMs: API_FAILURE_WINDOW_MS },
];
const OBSOLETE_LOCAL_PREFIXES = [
  "rm_cache_",
  "legacy_",
  "inventory-cache-",
  "api-cache-",
  "sw-cache-",
  "tmp_",
  "temp_",
];
const SESSION_ALLOWED_KEYS = new Set([BUILD_RELOAD_GUARD_KEY]);
const COOKIE_PREFIXES_TO_CLEAR = ["legacy_", "tmp_", "cache_"];
let interceptorAttached = false;
let maintenanceInitialized = false;

const getNow = () => Date.now();

function safelyParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function setTimestamp(key) {
  localStorage.setItem(key, String(getNow()));
}

function shouldRun(lastRun, intervalMs) {
  return !lastRun || getNow() - Number(lastRun) > intervalMs;
}

function removeExpiredTempLocalStorage() {
  TEMP_LOCAL_KEY_RULES.forEach(({ key, ttlMs }) => {
    const rawValue = localStorage.getItem(key);
    if (!rawValue) return;
    const parsed = safelyParse(rawValue);
    const ts = parsed?.ts || parsed?.timestamp || Number(localStorage.getItem(`${key}_ts`));
    if (ts && getNow() - Number(ts) > ttlMs) {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_ts`);
    }
  });
}

function purgeObsoleteLocalStorage({ preserveSession = true } = {}) {
  const preserved = new Set(KNOWN_LOCAL_KEYS);
  if (!preserveSession) {
    preserved.delete("auth");
    preserved.delete("role");
  }

  Object.keys(localStorage).forEach((key) => {
    if (preserved.has(key)) return;
    if (TEMP_LOCAL_KEY_RULES.some((rule) => rule.key === key)) return;
    if (OBSOLETE_LOCAL_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
  });
}

function cleanupSessionStorage() {
  Object.keys(sessionStorage).forEach((key) => {
    if (!SESSION_ALLOWED_KEYS.has(key)) {
      sessionStorage.removeItem(key);
    }
  });
}

function clearObsoleteCookies() {
  if (typeof document === "undefined") return;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  cookies.forEach((cookie) => {
    const [rawName] = cookie.split("=");
    const name = rawName?.trim();
    if (!name) return;
    if (["session_token"].includes(name)) return;
    if (COOKIE_PREFIXES_TO_CLEAR.some((prefix) => name.startsWith(prefix))) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
    }
  });
}

async function cleanupCaches({ deep = false } = {}) {
  if (!("caches" in window)) return;
  const names = await caches.keys();
  await Promise.all(
    names.map((name) => {
      const isCurrent = CURRENT_CACHE_NAMES.includes(name);
      const isLegacy = name.startsWith("redmagma-") || name.startsWith("inventory-cache-");
      if (!isCurrent && isLegacy) {
        return caches.delete(name);
      }
      return Promise.resolve(false);
    })
  );

  if (deep) {
    const runtimeCache = await caches.open(CURRENT_CACHE_NAMES[1]);
    const keys = await runtimeCache.keys();
    await Promise.all(
      keys
        .filter((request) => request.url.includes("/api/"))
        .map((request) => runtimeCache.delete(request))
    );
  }
}

async function pruneIndexedDb({ deep = false } = {}) {
  if (!deep || !window.indexedDB || typeof window.indexedDB.databases !== "function") return;
  const dbs = await window.indexedDB.databases();
  const deletions = (dbs || [])
    .filter((db) => db?.name && /^(redmagma-|inventory-|rm-legacy)/i.test(db.name))
    .map((db) => new Promise((resolve) => {
      const req = window.indexedDB.deleteDatabase(db.name);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    }));
  await Promise.all(deletions);
}

async function refreshServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.update().catch(() => null)));
}

export async function runClientStorageCleanup(options = {}) {
  const { deep = false, preserveSession = true } = options;
  removeExpiredTempLocalStorage();
  purgeObsoleteLocalStorage({ preserveSession });
  cleanupSessionStorage();
  clearObsoleteCookies();
  await cleanupCaches({ deep });
  await pruneIndexedDb({ deep });
  await refreshServiceWorker();
  localStorage.setItem(STORAGE_VERSION_KEY, APP_STORAGE_VERSION);
  localStorage.setItem(BUILD_VERSION_KEY, APP_BUILD_VERSION);
  setTimestamp(LAST_CLEANUP_KEY);
  if (deep) setTimestamp(LAST_DEEP_CLEANUP_KEY);
}

async function runHealthMonitor() {
  const issues = [];
  if (localStorage.getItem(STORAGE_VERSION_KEY) !== APP_STORAGE_VERSION) issues.push("schema_mismatch");
  if (localStorage.getItem(BUILD_VERSION_KEY) !== APP_BUILD_VERSION) issues.push("build_mismatch");

  if (navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate();
    if ((estimate?.usage || 0) > MAX_STORAGE_USAGE_BYTES) issues.push("storage_oversize");
  }

  if ("caches" in window) {
    const cacheNames = await caches.keys();
    const versioned = cacheNames.filter((name) => name.startsWith("redmagma-") || name.startsWith("inventory-cache-"));
    if (versioned.length > CURRENT_CACHE_NAMES.length) issues.push("cache_version_conflict");
  }

  if (issues.length) {
    await runClientStorageCleanup({ deep: issues.includes("storage_oversize") || issues.includes("cache_version_conflict") });
  }
}

async function runScheduledCleanup() {
  const lastCleanup = localStorage.getItem(LAST_CLEANUP_KEY);
  const lastDeepCleanup = localStorage.getItem(LAST_DEEP_CLEANUP_KEY);
  if (shouldRun(lastCleanup, STANDARD_CLEANUP_INTERVAL_MS)) {
    await runClientStorageCleanup();
  }
  if (shouldRun(lastDeepCleanup, DEEP_CLEANUP_INTERVAL_MS)) {
    await runClientStorageCleanup({ deep: true });
  }
}

async function runVersionSelfHeal() {
  const storedBuild = localStorage.getItem(BUILD_VERSION_KEY);
  const storedSchema = localStorage.getItem(STORAGE_VERSION_KEY);
  const buildChanged = storedBuild && storedBuild !== APP_BUILD_VERSION;
  const schemaChanged = storedSchema && storedSchema !== APP_STORAGE_VERSION;
  if (!buildChanged && !schemaChanged) {
    localStorage.setItem(BUILD_VERSION_KEY, APP_BUILD_VERSION);
    localStorage.setItem(STORAGE_VERSION_KEY, APP_STORAGE_VERSION);
    return;
  }

  await runClientStorageCleanup({ deep: true });
  const guard = sessionStorage.getItem(BUILD_RELOAD_GUARD_KEY);
  if (guard !== APP_BUILD_VERSION) {
    sessionStorage.setItem(BUILD_RELOAD_GUARD_KEY, APP_BUILD_VERSION);
    window.location.reload();
  }
}

export async function initializeClientStorageMaintenance() {
  if (typeof window === "undefined") return;
  if (maintenanceInitialized) return;
  maintenanceInitialized = true;
  window.setTimeout(async () => {
    await runVersionSelfHeal();
    await runHealthMonitor();
    await runScheduledCleanup();
  }, 0);
}

export async function forceStorageCleanupAndReload() {
  await runClientStorageCleanup({ deep: true, preserveSession: true });
  window.location.reload();
}

async function safeSelfHeal(reason) {
  const lastHeal = Number(localStorage.getItem(SELF_HEAL_GUARD_KEY) || 0);
  if (getNow() - lastHeal < SELF_HEAL_COOLDOWN_MS) return;
  localStorage.setItem(SELF_HEAL_GUARD_KEY, String(getNow()));
  await runClientStorageCleanup({ deep: true, preserveSession: true });
  if (reason === "repeated_api_failures") {
    window.location.reload();
  }
}

export function attachStorageHealthInterceptors(axiosInstance) {
  if (interceptorAttached) return;
  interceptorAttached = true;
  axiosInstance.interceptors.response.use(
    (response) => {
      if (response?.config?.url?.includes("/api/")) {
        localStorage.removeItem(API_FAILURE_COUNT_KEY);
        localStorage.removeItem(API_FAILURE_TS_KEY);
      }
      return response;
    },
    async (error) => {
      const url = error?.config?.url || "";
      const status = error?.response?.status;
      const isApi = url.includes("/api/");
      if (isApi && status !== 401 && status !== 403) {
        const firstTs = Number(localStorage.getItem(API_FAILURE_TS_KEY) || 0);
        if (!firstTs || getNow() - firstTs > API_FAILURE_WINDOW_MS) {
          localStorage.setItem(API_FAILURE_TS_KEY, String(getNow()));
          localStorage.setItem(API_FAILURE_COUNT_KEY, "1");
        } else {
          const nextCount = Number(localStorage.getItem(API_FAILURE_COUNT_KEY) || 0) + 1;
          localStorage.setItem(API_FAILURE_COUNT_KEY, String(nextCount));
          if (nextCount >= API_FAILURE_THRESHOLD) {
            await safeSelfHeal("repeated_api_failures");
          }
        }
      }
      return Promise.reject(error);
    }
  );
}

export function registerAppServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(`/sw.js?build=${APP_BUILD_VERSION}`).catch(() => null);
  });
}

export const storageAuditSummary = {
  appStorageVersion: APP_STORAGE_VERSION,
  appBuildVersion: APP_BUILD_VERSION,
  standardCleanupIntervalMs: STANDARD_CLEANUP_INTERVAL_MS,
  deepCleanupIntervalMs: DEEP_CLEANUP_INTERVAL_MS,
  protectedLocalKeys: Array.from(KNOWN_LOCAL_KEYS),
  tempLocalKeyRules: TEMP_LOCAL_KEY_RULES,
  cacheNames: CURRENT_CACHE_NAMES,
};